import { useState, useEffect, useRef } from "react";
import {
  TbX, TbTag, TbShield, TbShieldOff, TbShieldHalf,
  TbToolsKitchen2, TbUsers, TbClock,
  TbPercentage, TbCheck, TbMoodKid,
  TbUserPlus, TbHome, TbDoor, TbLink, TbLinkOff,
  TbReceipt, TbReceiptOff, TbCurrencyEuro,
  TbBed, TbCalendar, TbMoon
} from "react-icons/tb";

const Field = ({ label, hint, children }) => (
  <div className="flex flex-col gap-1.5">
    <label className="text-[11px] font-semibold text-gray-400 uppercase tracking-widest">{label}</label>
    {children}
    {hint && <p className="text-[11px] text-gray-400">{hint}</p>}
  </div>
);

const Input = ({ icon: Icon, ...props }) => (
  <div className="relative">
    {Icon && <Icon className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-base pointer-events-none" />}
    <input {...props} className={`w-full ${Icon ? "pl-9" : "pl-4"} pr-4 py-2.5 bg-gray-50 border border-gray-100 rounded-xl text-sm text-gray-800 outline-none focus:border-[#3A9D23] focus:bg-white transition-all duration-150 placeholder:text-gray-300`} />
  </div>
);

const NumInput = ({ icon: Icon, suffix, ...props }) => (
  <div className="relative">
    {Icon && <Icon className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-base pointer-events-none" />}
    <input type="number" {...props} className={`w-full ${Icon ? "pl-9" : "pl-4"} ${suffix ? "pr-12" : "pr-4"} py-2.5 bg-gray-50 border border-gray-100 rounded-xl text-sm text-gray-800 outline-none focus:border-[#3A9D23] focus:bg-white transition-all duration-150`} />
    {suffix && <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-400 pointer-events-none">{suffix}</span>}
  </div>
);

const SectionTitle = ({ icon: Icon, label }) => (
  <div className="flex items-center gap-2 pt-2">
    <div className="w-6 h-6 rounded-lg bg-[#3A9D23]/10 flex items-center justify-center shrink-0">
      <Icon className="text-[#3A9D23] text-sm" />
    </div>
    <span className="text-xs font-bold text-gray-500 uppercase tracking-widest">{label}</span>
    <div className="flex-1 h-px bg-gray-100" />
  </div>
);

const PolicyCard = ({ opt, selected, onClick }) => (
  <div onClick={onClick} className={`relative flex items-start gap-3 p-3.5 rounded-xl border cursor-pointer transition-all duration-150 select-none ${selected ? opt.activeClass : "border-gray-100 bg-gray-50 hover:border-gray-200 hover:bg-white"}`}>
    <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 mt-0.5 ${opt.iconBg}`}>
      <opt.Icon className={`text-base ${opt.iconColor}`} />
    </div>
    <div className="flex-1 min-w-0">
      <p className="text-sm font-semibold text-gray-800">{opt.label}</p>
      <p className="text-xs text-gray-400 mt-0.5">{opt.desc}</p>
    </div>
    <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center shrink-0 mt-1 transition-all ${selected ? "border-[#3A9D23] bg-[#3A9D23]" : "border-gray-300"}`}>
      {selected && <TbCheck className="text-white text-[10px] stroke-[3]" />}
    </div>
  </div>
);

// 🆕 LONG SÉJOUR — carte de sélection du type de séjour (même style que PolicyCard)
const StayTypeCard = ({ opt, selected, onClick }) => (
  <div onClick={onClick} className={`relative flex items-start gap-3 p-3.5 rounded-xl border cursor-pointer transition-all duration-150 select-none ${selected ? opt.activeClass : "border-gray-100 bg-gray-50 hover:border-gray-200 hover:bg-white"}`}>
    <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 mt-0.5 ${opt.iconBg}`}>
      <opt.Icon className={`text-base ${opt.iconColor}`} />
    </div>
    <div className="flex-1 min-w-0">
      <p className="text-sm font-semibold text-gray-800">{opt.label}</p>
      <p className="text-xs text-gray-400 mt-0.5">{opt.desc}</p>
    </div>
    <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center shrink-0 mt-1 transition-all ${selected ? "border-[#3A9D23] bg-[#3A9D23]" : "border-gray-300"}`}>
      {selected && <TbCheck className="text-white text-[10px] stroke-[3]" />}
    </div>
  </div>
);

// 🆕 FRAIS FIXES — carte de sélection du mode de pénalité (% vs €), même style que le toggle taxe
const PenaltyModeCard = ({ label, desc, selected, onClick }) => (
  <div onClick={onClick} className={`flex flex-col gap-0.5 px-3 py-2.5 rounded-xl border cursor-pointer transition-all duration-150 select-none ${selected ? "border-[#3A9D23] bg-[#3A9D23]/5" : "border-gray-100 bg-gray-50 hover:border-gray-200 hover:bg-white"}`}>
    <div className="flex items-center justify-between">
      <p className={`text-sm font-semibold ${selected ? "text-gray-800" : "text-gray-500"}`}>{label}</p>
      <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center transition-all ${selected ? "border-[#3A9D23] bg-[#3A9D23]" : "border-gray-300"}`}>
        {selected && <TbCheck className="text-white text-[10px] stroke-[3]" />}
      </div>
    </div>
    <p className="text-[10px] text-gray-400">{desc}</p>
  </div>
);

const MealChip = ({ opt, selected, onClick }) => (
  <button type="button" onClick={onClick} className={`flex items-center gap-2 px-3 py-2.5 rounded-xl border text-sm transition-all duration-150 ${selected ? "border-[#3A9D23] bg-[#3A9D23]/5 text-[#3A9D23] font-semibold" : "border-gray-100 bg-gray-50 text-gray-600 hover:border-gray-200 hover:bg-white"}`}>
    {opt.emoji && <span className="text-base">{opt.emoji}</span>}
    <span>{opt.label}</span>
  </button>
);

const RoomChip = ({ room, checked, hasPrice, loading, onChange }) => (
  <button type="button" onClick={() => onChange(room._id, !checked)} disabled={loading}
    className={`relative flex items-center gap-2.5 px-3.5 py-2.5 rounded-xl border text-left transition-all duration-150 select-none w-full ${loading ? "opacity-50 cursor-wait" : "cursor-pointer"} ${checked ? "border-[#3A9D23] bg-[#3A9D23]/5" : "border-gray-100 bg-gray-50 hover:border-gray-200 hover:bg-white"}`}>
    <div className={`w-7 h-7 rounded-lg flex items-center justify-center shrink-0 ${checked ? "bg-[#3A9D23]/15" : "bg-gray-100"}`}>
      <TbDoor className={`text-sm ${checked ? "text-[#3A9D23]" : "text-gray-400"}`} />
    </div>
    <div className="flex-1 min-w-0">
      <p className={`text-sm font-semibold truncate ${checked ? "text-gray-800" : "text-gray-600"}`}>
        {room.title || `Chambre ${room._id.slice(-4)}`}
      </p>
      {checked && (
        <p className="text-[10px] mt-0.5 flex items-center gap-1">
          {hasPrice
            ? <><TbLink className="text-[#3A9D23]" /><span className="text-[#3A9D23]">Prix configuré</span></>
            : <><TbLinkOff className="text-amber-400" /><span className="text-amber-500">Associée — prix à définir</span></>
          }
        </p>
      )}
    </div>
    <div className={`w-4 h-4 rounded border-2 flex items-center justify-center shrink-0 transition-all ${checked ? "border-[#3A9D23] bg-[#3A9D23]" : "border-gray-300"}`}>
      {checked && <TbCheck className="text-white text-[10px] stroke-[3]" />}
    </div>
  </button>
);

const POLICY_OPTIONS = [
  { value: "free", label: "Annulation gratuite", desc: "Le client annule sans frais dans les délais", Icon: TbShield, iconBg: "bg-green-50", iconColor: "text-green-500", activeClass: "border-green-200 bg-green-50/60" },
  { value: "partial", label: "Annulation partielle", desc: "Des frais s'appliquent après le délai", Icon: TbShieldHalf, iconBg: "bg-amber-50", iconColor: "text-amber-500", activeClass: "border-amber-200 bg-amber-50/60" },
  { value: "non_refundable", label: "Non remboursable", desc: "Aucun remboursement possible", Icon: TbShieldOff, iconBg: "bg-red-50", iconColor: "text-red-400", activeClass: "border-red-200 bg-red-50/60" }
];

// 🆕 LONG SÉJOUR — options du type de séjour
const STAY_TYPE_OPTIONS = [
  { value: "short_stay", label: "Court séjour", desc: "Tarif à la nuit, visible dès 1 nuit", Icon: TbBed, iconBg: "bg-blue-50", iconColor: "text-blue-400", activeClass: "border-blue-200 bg-blue-50/60" },
  { value: "long_stay", label: "Long séjour (mensuel)", desc: "Tarif réduit, visible à partir d'un seuil de nuits", Icon: TbCalendar, iconBg: "bg-[#3A9D23]/10", iconColor: "text-[#3A9D23]", activeClass: "border-[#3A9D23]/30 bg-[#3A9D23]/5" }
];

const MEAL_OPTIONS = [
  { value: "none", label: "Sans repas", emoji: null },
  { value: "breakfast", label: "Petit-déjeuner", emoji: "🍳" },
  { value: "half_board", label: "Demi-pension", emoji: "🍽️" },
  { value: "full_board", label: "Pension complète", emoji: "🍱" }
];

const DEFAULT_STATE = {
  name: "", description: "", mealPlan: "none",
  stayType: "short_stay", minNights: 30,   // 🆕 LONG SÉJOUR
  policyType: "free",
  penaltyMode: "percentage",     // 🆕 FRAIS FIXES
  penaltyPercentage: 30,
  penaltyFixedAmount: 20,        // 🆕 FRAIS FIXES
  hoursBeforeArrival: 24,
  baseOccupancy: 1, extraAdultFee: 0, extraChildFee: 0, childMaxAge: 12,
  taxEnabled: false, taxAmount: 2.05, taxIncluded: false,
};

const RatePlansDrawer = ({
  show, onHide, onCreate, onUpdate, hotelId, editingPlan,
  rooms = [], ratePrices = [],
}) => {
  const [form, setForm] = useState(DEFAULT_STATE);
  const [mounted, setMounted] = useState(false);
  const drawerRef = useRef(null);
  const [roomPriceMap, setRoomPriceMap] = useState({});  // roomId → ratePriceId (tous, actifs + inactifs)
  const [checkedRooms, setCheckedRooms] = useState(new Set()); // seulement les actifs
  const [loadingRooms, setLoadingRooms] = useState(new Set());
  const isEdit = Boolean(editingPlan);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e?.target ? e.target.value : e }));

  useEffect(() => {
    if (!show) { setMounted(false); return; }
    requestAnimationFrame(() => setMounted(true));

    if (editingPlan) {
      setForm({
        name: editingPlan.name || "",
        description: editingPlan.description || "",
        mealPlan: editingPlan.mealPlan || "none",
        stayType: editingPlan.stayType || "short_stay",      // 🆕 LONG SÉJOUR
        minNights: editingPlan.minNights || 30,              // 🆕 LONG SÉJOUR
        policyType: editingPlan.cancellationPolicy?.type || "free",
        penaltyMode: editingPlan.cancellationPolicy?.penaltyMode || "percentage",           // 🆕 FRAIS FIXES
        penaltyPercentage: editingPlan.cancellationPolicy?.penaltyPercentage || 30,
        penaltyFixedAmount: editingPlan.cancellationPolicy?.penaltyFixedAmount || 20,        // 🆕 FRAIS FIXES
        hoursBeforeArrival: editingPlan.cancellationPolicy?.hoursBeforeArrival || 24,
        baseOccupancy: editingPlan.extraGuestPolicy?.baseOccupancy || 1,
        extraAdultFee: editingPlan.extraGuestPolicy?.extraAdultFee || 0,
        extraChildFee: editingPlan.extraGuestPolicy?.extraChildFee || 0,
        childMaxAge: editingPlan.extraGuestPolicy?.childMaxAge || 12,
        taxEnabled:  editingPlan.taxConfig?.enabled  ?? false,
        taxAmount:   editingPlan.taxConfig?.amount   ?? 2.05,
        taxIncluded: editingPlan.taxConfig?.included ?? false,
      });

      const map = {};
      const checked = new Set();
      // 🆕 ASSOCIATION — les chambres associées viennent de editingPlan.roomIds
      (editingPlan.roomIds || []).forEach((id) => {
        checked.add(id?.toString?.() ?? id);
      });
      // ratePrices sert UNIQUEMENT au badge "prix configuré" : on ne garde que les vrais prix actifs
      ratePrices.forEach((rp) => {
        if (rp.active !== false && rp.price > 0) {
          const rid = rp.roomId?.toString?.() ?? rp.roomId;
          map[rid] = rp._id;
        }
      });
      setRoomPriceMap(map);
      setCheckedRooms(checked);
    } else {
      setForm(DEFAULT_STATE);
      setRoomPriceMap({});
      setCheckedRooms(new Set());
    }
  }, [editingPlan, show, ratePrices]);

  const handleRoomToggle = (roomId, nowChecked) => {
    setCheckedRooms((prev) => {
      const next = new Set(prev);
      nowChecked ? next.add(roomId) : next.delete(roomId);
      return next;
    });
  };

  const handleSubmit = async () => {
    if (!form.name.trim()) return;

    const payload = {
      hotelId,
      name: form.name.trim(),
      description: form.description.trim(),
      mealPlan: form.mealPlan,
      // 🆕 LONG SÉJOUR — court séjour force minNights à 1 côté back, on envoie quand même proprement
      stayType: form.stayType,
      minNights: form.stayType === "long_stay" ? Math.max(2, Number(form.minNights) || 30) : 1,
      // 🆕 ASSOCIATION — l'association vit sur le plan. Plus aucun RatePrice créé ici.
      roomIds: [...checkedRooms],
      cancellationPolicy: {
        type: form.policyType,
        hoursBeforeArrival: form.policyType !== "non_refundable" ? Number(form.hoursBeforeArrival) : 0,
        // 🆕 FRAIS FIXES — seul le champ du mode actif est renseigné, l'autre reste à 0
        penaltyMode: form.policyType === "partial" ? form.penaltyMode : "percentage",
        penaltyPercentage: form.policyType === "partial" && form.penaltyMode === "percentage"
          ? Number(form.penaltyPercentage) : 0,
        penaltyFixedAmount: form.policyType === "partial" && form.penaltyMode === "fixed"
          ? Number(form.penaltyFixedAmount) : 0,
      },
      extraGuestPolicy: {
        baseOccupancy: Number(form.baseOccupancy),
        extraAdultFee: Number(form.extraAdultFee),
        extraChildFee: Number(form.extraChildFee),
        childMaxAge: Number(form.childMaxAge)
      },
      taxConfig: {
        enabled:  form.taxEnabled,
        amount:   Number(form.taxAmount),
        included: form.taxIncluded,
      },
    };

    // Le back gère tout : création/màj du plan + diff des associations (activation/désactivation
    // des RatePrice existants). Aucun prix n'est touché côté front.
    if (isEdit) {
      await onUpdate(editingPlan._id, payload);
    } else {
      await onCreate(payload);
    }

    setForm(DEFAULT_STATE);
    onHide();
  };

  const handleClose = () => {
    setMounted(false);
    setTimeout(() => { setForm(DEFAULT_STATE); onHide(); }, 200);
  };

  if (!show) return null;

  const checkedCount = checkedRooms.size;
  const isLongStay = form.stayType === "long_stay";   // 🆕 LONG SÉJOUR

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      <div className={`absolute inset-0 bg-black/25 backdrop-blur-[2px] transition-opacity duration-200 ${mounted ? "opacity-100" : "opacity-0"}`} onClick={handleClose} />

      <div ref={drawerRef} className={`relative w-full max-w-[420px] bg-white h-full flex flex-col shadow-[-20px_0_60px_rgba(0,0,0,0.12)] transition-transform duration-200 ease-out ${mounted ? "translate-x-0" : "translate-x-full"}`}>
        <div className="h-0.5 w-full bg-gradient-to-r from-[#3A9D23] via-[#5abf3a] to-[#3A9D23]/30" />

        {/* HEADER */}
        <div className="flex items-start justify-between px-6 pt-5 pb-4 border-b border-gray-100">
          <div>
            <div className="flex items-center gap-2 mb-0.5">
              <div className="w-5 h-5 rounded-md bg-[#3A9D23] flex items-center justify-center">
                <TbTag className="text-white text-xs" />
              </div>
              <h2 className="text-sm font-bold text-gray-900 tracking-tight">{isEdit ? "Modifier le tarif" : "Nouveau tarif"}</h2>
            </div>
            <p className="text-[11px] text-gray-400 ml-7">{isEdit ? "Mettez à jour la configuration tarifaire" : "Configurez votre plan tarifaire"}</p>
          </div>
          <button onClick={handleClose} className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-gray-100 transition text-gray-400 hover:text-gray-600 mt-0.5">
            <TbX className="text-base" />
          </button>
        </div>

        {/* CONTENT */}
        <div className="flex-1 overflow-y-auto px-6 py-5 flex flex-col gap-5">
          <SectionTitle icon={TbTag} label="Informations" />

          <Field label="Nom du tarif *">
            <Input icon={TbTag} value={form.name} onChange={set("name")} placeholder="Ex : Tarif flexible, Non remboursable..." required />
          </Field>

          <Field label="Description" hint="Visible par les clients lors de la réservation">
            <textarea value={form.description} onChange={set("description")} placeholder="Décrivez les avantages de ce tarif..." rows={2}
              className="w-full px-4 py-2.5 bg-gray-50 border border-gray-100 rounded-xl text-sm text-gray-800 outline-none focus:border-[#3A9D23] focus:bg-white transition-all resize-none placeholder:text-gray-300" />
          </Field>

          {/* 🆕 LONG SÉJOUR — Type de séjour */}
          <SectionTitle icon={TbCalendar} label="Type de séjour" />
          <Field label="Type de tarif *">
            <div className="flex flex-col gap-2">
              {STAY_TYPE_OPTIONS.map((opt) => (
                <StayTypeCard key={opt.value} opt={opt} selected={form.stayType === opt.value}
                  onClick={() => setForm((f) => {
                    // 🆕 LONG SÉJOUR — au passage en long séjour : force policyType "partial"
                    // (seule politique autorisée) et bascule le délai sur un défaut en jours (30j = 720h).
                    // Au retour en court séjour : remet un délai par défaut raisonnable en heures.
                    if (opt.value === "long_stay" && f.stayType !== "long_stay") {
                      return { ...f, stayType: "long_stay", policyType: "partial", hoursBeforeArrival: 720 };
                    }
                    if (opt.value === "short_stay" && f.stayType !== "short_stay") {
                      return { ...f, stayType: "short_stay", hoursBeforeArrival: 24 };
                    }
                    return { ...f, stayType: opt.value };
                  })} />
              ))}
            </div>
          </Field>

          {isLongStay && (
            <>
              <Field label="Nombre de nuits minimum" hint="Ce tarif ne s'affiche que si le client réserve au moins ce nombre de nuits">
                <NumInput icon={TbMoon} min={2} value={form.minNights} onChange={set("minNights")} suffix="nuits min." />
              </Field>
              <div className="bg-[#3A9D23]/5 border border-[#3A9D23]/15 rounded-xl px-4 py-3 flex flex-col gap-1.5">
                <p className="text-[11px] font-bold text-[#3A9D23] uppercase tracking-widest">Comment ça marche</p>
                <p className="text-xs text-gray-600">
                  Ce tarif est <span className="font-semibold text-gray-800">masqué</span> pour les séjours de moins de <span className="font-semibold text-gray-800">{form.minNights} nuits</span>.
                </p>
                <p className="text-xs text-gray-600">
                  Saisissez ensuite le prix/nuit réduit dans la grille tarifaire — le total mensuel se calcule automatiquement.
                </p>
                <p className="text-[11px] text-gray-400 mt-0.5">
                  💡 Conseil : associez-le à une annulation non remboursable ou partielle pour éviter qu'un client réserve au mois puis annule après quelques nuits.
                </p>
              </div>
            </>
          )}

          <SectionTitle icon={TbToolsKitchen2} label="Restauration" />
          <Field label="Repas inclus">
            <div className="grid grid-cols-2 gap-2">
              {MEAL_OPTIONS.map((opt) => (
                <MealChip key={opt.value} opt={opt} selected={form.mealPlan === opt.value}
                  onClick={() => setForm((f) => ({ ...f, mealPlan: opt.value }))} />
              ))}
            </div>
          </Field>

          <SectionTitle icon={TbShield} label="Annulation" />

          {/* 🆕 LONG SÉJOUR — sur un plan long séjour, seule "Annulation partielle" est autorisée :
              pas de gratuite (trop risqué pour l'hôtelier sur un engagement d'un mois), pas de
              non-remboursable (trop brutal pour le client). On masque les 2 autres cartes. */}
          {isLongStay && (
            <div className="bg-amber-50 border border-amber-100 rounded-xl px-4 py-3 flex items-start gap-3">
              <TbShieldHalf className="text-amber-500 text-base mt-0.5 shrink-0" />
              <p className="text-xs text-amber-700 leading-relaxed">
                Les tarifs long séjour imposent une annulation partielle : le client bénéficie d'une période gratuite, puis des frais s'appliquent après le délai.
              </p>
            </div>
          )}

          <Field label="Politique d'annulation *">
            <div className="flex flex-col gap-2">
              {(isLongStay ? POLICY_OPTIONS.filter((opt) => opt.value === "partial") : POLICY_OPTIONS).map((opt) => (
                <PolicyCard key={opt.value} opt={opt} selected={form.policyType === opt.value}
                  onClick={() => setForm((f) => ({ ...f, policyType: opt.value }))} />
              ))}
            </div>
          </Field>

          {form.policyType !== "non_refundable" && (
            // 🆕 LONG SÉJOUR — le délai s'affiche/se saisit en jours (comme Airbnb "30 jours avant
            // l'arrivée"), converti en heures en interne pour ne rien casser côté calcul backend.
            <Field label="Délai d'annulation gratuite" hint="Annulation sans frais si effectuée avant ce délai">
              <NumInput
                icon={TbClock}
                min={0}
                value={isLongStay ? Math.round(form.hoursBeforeArrival / 24) : form.hoursBeforeArrival}
                onChange={(e) => {
                  const val = Number(e.target.value) || 0;
                  setForm((f) => ({ ...f, hoursBeforeArrival: isLongStay ? val * 24 : val }));
                }}
                suffix={isLongStay ? "jours" : "heures"}
              />
            </Field>
          )}

          {/* 🆕 FRAIS FIXES — toggle % vs € pour l'annulation partielle */}
          {form.policyType === "partial" && (
            <>
              <Field label="Type de frais" hint="Comment les frais d'annulation hors délai sont calculés">
                <div className="grid grid-cols-2 gap-2">
                  <PenaltyModeCard
                    label="Pourcentage"
                    desc="% du montant total"
                    selected={form.penaltyMode === "percentage"}
                    onClick={() => setForm((f) => ({ ...f, penaltyMode: "percentage" }))}
                  />
                  <PenaltyModeCard
                    label="Montant fixe"
                    desc="Somme en €"
                    selected={form.penaltyMode === "fixed"}
                    onClick={() => setForm((f) => ({ ...f, penaltyMode: "fixed" }))}
                  />
                </div>
              </Field>

              {form.penaltyMode === "percentage" ? (
                <Field label="Frais d'annulation" hint="Pourcentage prélevé du montant total si annulation hors délai">
                  <NumInput icon={TbPercentage} min={1} max={100} value={form.penaltyPercentage} onChange={set("penaltyPercentage")} suffix="%" />
                </Field>
              ) : (
                <Field label="Frais d'annulation" hint="Montant fixe retenu si annulation hors délai">
                  <NumInput icon={TbCurrencyEuro} min={1} step={0.01} value={form.penaltyFixedAmount} onChange={set("penaltyFixedAmount")} suffix="€" />
                </Field>
              )}
            </>
          )}

          <SectionTitle icon={TbUsers} label="Occupants" />
          <Field label="Occupation de base incluse" hint="Nombre de personnes incluses dans le tarif de base">
            <NumInput icon={TbHome} min={1} max={10} value={form.baseOccupancy} onChange={set("baseOccupancy")} suffix="pers." />
          </Field>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Supplément adulte" hint={`Par nuit, au-delà de ${form.baseOccupancy} pers.`}>
              <NumInput icon={TbUserPlus} min={0} value={form.extraAdultFee} onChange={set("extraAdultFee")} suffix="€" />
            </Field>
            <Field label="Supplément enfant" hint="Par enfant, par nuit">
              <NumInput icon={TbMoodKid} min={0} value={form.extraChildFee} onChange={set("extraChildFee")} suffix="€" />
            </Field>
          </div>

          <Field label="Âge maximum enfant" hint="En dessous de cet âge → tarif enfant">
            <NumInput icon={TbMoodKid} min={0} max={17} value={form.childMaxAge} onChange={set("childMaxAge")} suffix="ans" />
          </Field>

          {(Number(form.extraAdultFee) > 0 || Number(form.extraChildFee) > 0) && (
            <div className="bg-[#3A9D23]/5 border border-[#3A9D23]/15 rounded-xl px-4 py-3 flex flex-col gap-1.5">
              <p className="text-[11px] font-bold text-[#3A9D23] uppercase tracking-widest">Récapitulatif</p>
              <p className="text-xs text-gray-600">Tarif de base pour <span className="font-semibold text-gray-800">{form.baseOccupancy} personne{form.baseOccupancy > 1 ? "s" : ""}</span></p>
              {Number(form.extraAdultFee) > 0 && <p className="text-xs text-gray-600">+ <span className="font-semibold text-gray-800">{form.extraAdultFee} €</span>/nuit par adulte supplémentaire</p>}
              {Number(form.extraChildFee) > 0 && <p className="text-xs text-gray-600">+ <span className="font-semibold text-gray-800">{form.extraChildFee} €</span>/nuit par enfant (moins de {form.childMaxAge} ans)</p>}
            </div>
          )}

          <SectionTitle icon={TbReceipt} label="Taxe de séjour" />
          <Field hint="Taxe légale comorienne — 1 personne · 1 nuit">
            <div onClick={() => setForm(f => ({ ...f, taxEnabled: !f.taxEnabled }))}
              className={`flex items-center justify-between px-4 py-3 rounded-xl border cursor-pointer transition-all duration-150 select-none ${form.taxEnabled ? "border-[#3A9D23] bg-[#3A9D23]/5" : "border-gray-100 bg-gray-50 hover:border-gray-200"}`}>
              <div className="flex items-center gap-3">
                <div className={`w-7 h-7 rounded-lg flex items-center justify-center shrink-0 ${form.taxEnabled ? "bg-[#3A9D23]/15" : "bg-gray-100"}`}>
                  {form.taxEnabled ? <TbReceipt className="text-[#3A9D23] text-sm" /> : <TbReceiptOff className="text-gray-400 text-sm" />}
                </div>
                <div>
                  <p className={`text-sm font-semibold ${form.taxEnabled ? "text-gray-800" : "text-gray-500"}`}>{form.taxEnabled ? "Taxe activée" : "Taxe désactivée"}</p>
                  <p className="text-[10px] text-gray-400">Par personne · par nuit</p>
                </div>
              </div>
              <div className={`w-9 h-5 rounded-full transition-all duration-200 relative ${form.taxEnabled ? "bg-[#3A9D23]" : "bg-gray-200"}`}>
                <div className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-all duration-200 ${form.taxEnabled ? "left-4" : "left-0.5"}`} />
              </div>
            </div>
          </Field>

          {form.taxEnabled && (
            <>
              <Field label="Montant de la taxe">
                <NumInput icon={TbCurrencyEuro} min={0} step={0.01} value={form.taxAmount} onChange={set("taxAmount")} suffix="€ / pers. / nuit" />
              </Field>
              <Field label="Mode d'affichage">
                <div className="grid grid-cols-2 gap-2">
                  {[
                    { value: false, label: "En supplément", desc: "Affichée séparément" },
                    { value: true,  label: "Incluse", desc: "Dans le prix total" },
                  ].map(opt => (
                    <div key={String(opt.value)} onClick={() => setForm(f => ({ ...f, taxIncluded: opt.value }))}
                      className={`flex flex-col gap-0.5 px-3 py-2.5 rounded-xl border cursor-pointer transition-all duration-150 select-none ${form.taxIncluded === opt.value ? "border-[#3A9D23] bg-[#3A9D23]/5" : "border-gray-100 bg-gray-50 hover:border-gray-200 hover:bg-white"}`}>
                      <div className="flex items-center justify-between">
                        <p className={`text-sm font-semibold ${form.taxIncluded === opt.value ? "text-gray-800" : "text-gray-500"}`}>{opt.label}</p>
                        <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center transition-all ${form.taxIncluded === opt.value ? "border-[#3A9D23] bg-[#3A9D23]" : "border-gray-300"}`}>
                          {form.taxIncluded === opt.value && <TbCheck className="text-white text-[10px] stroke-[3]" />}
                        </div>
                      </div>
                      <p className="text-[10px] text-gray-400">{opt.desc}</p>
                    </div>
                  ))}
                </div>
              </Field>
              <div className="bg-[#3A9D23]/5 border border-[#3A9D23]/15 rounded-xl px-4 py-3 flex flex-col gap-1">
                <p className="text-[11px] font-bold text-[#3A9D23] uppercase tracking-widest">Aperçu</p>
                <p className="text-xs text-gray-600">Taxe de <span className="font-semibold text-gray-800">{form.taxAmount} €</span> / personne / nuit</p>
                <p className="text-xs text-gray-500">{form.taxIncluded ? "→ Le prix affiché inclut déjà la taxe" : "→ La taxe s'ajoutera au prix de la chambre"}</p>
              </div>
            </>
          )}

          {/* ── Chambres associées ── */}
          {rooms.length > 0 && (
            <>
              <SectionTitle icon={TbDoor} label="Chambres associées" />
              <Field label={`Chambres (${checkedCount}/${rooms.length})`} hint="Les associations seront appliquées à la validation du formulaire">
                <div className="flex flex-col gap-2">
                  {rooms.map((room) => {
                    const rid = room._id?.toString?.() ?? room._id;
                    const isChecked = checkedRooms.has(rid);
                    // ✅ hasPrice = coché ET dans roomPriceMap (prix existant actif ou inactif)
                    const hasPrice = isChecked && Boolean(roomPriceMap[rid]);
                    return (
                      <RoomChip key={rid} room={room} checked={isChecked} hasPrice={hasPrice}
                        loading={loadingRooms.has(rid)} onChange={handleRoomToggle} />
                    );
                  })}
                </div>
              </Field>

              {/* ✅ Bandeau amber : chambres cochées sans aucun RatePrice (même inactif) */}
              {isEdit && checkedCount > 0 && [...checkedRooms].some((rid) => !roomPriceMap[rid]) && (
                <div className="bg-amber-50 border border-amber-100 rounded-xl px-4 py-3 flex items-start gap-3">
                  <span className="text-amber-400 text-base mt-0.5">⚠️</span>
                  <p className="text-xs text-amber-700 leading-relaxed">
                    Certaines chambres sont associées mais n'ont pas encore de prix défini. Configurez-les depuis la grille tarifaire.
                  </p>
                </div>
              )}
            </>
          )}
        </div>

        {/* FOOTER */}
        <div className="px-6 py-4 border-t border-gray-100 bg-white">
          <button onClick={handleSubmit} disabled={!form.name.trim()}
            className={`w-full py-2.5 rounded-xl text-sm font-semibold transition-all duration-150 ${!form.name.trim() ? "bg-gray-100 text-gray-300 cursor-not-allowed" : "bg-[#3A9D23] text-white hover:bg-[#33891E] active:scale-[0.98] shadow-sm shadow-[#3A9D23]/20"}`}>
            {isEdit ? "Mettre à jour le tarif" : "Créer le tarif"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default RatePlansDrawer;